import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import iframeMixin from '@/mixins/iframe.js';
import iframeCommonListenersMixin from '@/mixins/iframeCommonListeners';
import automationMixin from '@/mixins/automation.js';

import { Iframe, PageLoaderWrapper } from '@/styles/common.styled.component';

import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

import {
    map,
    distinctUntilChanged,
    filter,
    debounceTime,
    switchMap,
    shareReplay,
} from 'rxjs/operators';

import { isValueExist } from '@/utils/utils';
import RingLoader from '@/components/Common/RingLoader';
import { REACT_BASE_URL } from '@/utils/consts';
import WithAccountModal from '@/components/hocs/WithAccountModal';

const ReactAutomation = {
    name: 'ReactAutomation',
    mixins: [iframeMixin, automationMixin, iframeCommonListenersMixin],
    watch: {},
    methods: {
        ...mapMutations('newDashboard', [
            'setTableAdAccounts',
            'setTeamspaceAllowedChannelIds',
            'setTeamspaceAllowedAssetIds',
            'setSelectedTab',
            'updateEditingWidget',
            'setViews',
            'setDimensions',
            'setWidgetError',
            'setWidgetStatusPanelWidgetID',
            'setDataSources',
            'setMetricAliases',
        ]),
        ...mapActions('newDashboard', [
            'getTableAdAccounts',
            'getDimensionList',
        ]),
        ...mapMutations('view', [
            'addNotiMsg',
            'setNotiMsgs',
            'setLogoutModalActive',
            'setIsNewCampaignModalActive',
        ]),
        ...mapActions('iframe', [
            'postIframeMessage',
            'addIframeRef',
            'removeIframeRef',
        ]),
        ...mapActions('user', ['getBoAccessAccount', 'fetchMembers']),
        ...mapMutations('user', ['setDashboardClient']),
        observeQuery$() {
            const query$ = this.generateWatchNew$('$route.query', {
                deep: true,
            }).pipe(distinctUntilChanged(R.equals), shareReplay(1));

            this.$subscribeTo(query$, (query) => {
                let userAccountIdFromQuery = query.userAccountId;

                if (userAccountIdFromQuery == 0) {
                    userAccountIdFromQuery = undefined;
                }
                if (
                    // fill userAccountId if not exist for debugging purpose
                    (!userAccountIdFromQuery && query.accountId) ||
                    // if user is not admin, can not use other userAccountId
                    (!this.isAdmin &&
                        userAccountIdFromQuery !== query.accountId)
                ) {
                    this.$router.replace({
                        query: R.assoc('userAccountId', query.accountId, query),
                    });
                }
            });
        },
        fetchData$() {
            const { generateWatchNew$: gen$, dashboardClientCreated$ } = this;

            const combined$ = dashboardClientCreated$.pipe(
                filter(R.identity),
                switchMap(() => gen$('_accountId')),
                map(Number),
                filter(R.identity),
                debounceTime(50),
                distinctUntilChanged()
            );

            this.$subscribeTo(combined$, (userAccountId) => {
                this.fetchMembers();
                this.getDimensionList();

                if (this.isAdmin && userAccountId) {
                    this.getBoAccessAccount(userAccountId);
                }
            });
        },
        openConnectionPanel() {
            this.connectionPanel = true;
        },
        sync$() {
            const { generateWatchNew$: gen$ } = this;
            const inited$ = gen$('cAccountId').pipe(
                filter(R.identity),
                debounceTime(200),
                shareReplay(1)
            );

            this.$subscribeTo(inited$, () => {
                this.addIframeRef(this.$refs.iframeReactDashboard_);
            });

            const user$ = inited$.pipe(
                switchMap(() => gen$('globalUser').pipe(filter(isValueExist)))
            );

            this.$subscribeTo(user$, (user) => {
                this.postIframeMessage({
                    type: `SYNC_USER`,
                    origin: `VUE`,
                    data: {
                        user,
                    },
                });
            });

            const account$ = inited$.pipe(
                switchMap(() => gen$('account').pipe(filter(isValueExist)))
            );

            this.$subscribeTo(account$, (account) => {
                this.postIframeMessage({
                    type: `SYNC_ACCOUNT`,
                    origin: `VUE`,
                    data: {
                        account,
                    },
                });
            });

            const route$ = inited$.pipe(switchMap(() => gen$('$route')));

            this.$subscribeTo(route$, (route) => {
                const { params = {}, query = {} } = route;
                if (params?.id && !query?.update) {
                    this.postIframeMessage({
                        type: `SYNC_ROUTE`,
                        origin: `VUE`,
                        data: {
                            query: {
                                ...R.pick(
                                    ['accountId', 'userAccountId'],
                                    this.$route.query
                                ),
                                ...query,
                                viewId: params?.id,
                            },
                        },
                    });
                }
            });
        },
    },
    data() {
        return {
            queryParams: undefined,
            currentId: undefined,
            openCreativeModal: false,
            connectionPanel: false,
        };
    },
    mounted() {
        this.sync$();
        this.currentId = this.$route.params.id;
        this.queryParams = this.$route.query;

        document.querySelector('html').classList.add('overflow-hidden');

        this.createClient$();

        this.$nextTick(() => {
            this.observeQuery$();
            this.fetchData$();
        });
    },
    beforeDestroy() {
        this.removeIframeRef(this.$refs.iframeReactDashboard_);
        document.querySelector('html').classList.remove('overflow-hidden');
        this.dashboardClientCreated$.next(false);
    },
    computed: {
        ...mapGetters('user', [
            'isAdmin',
            'userId',
            'planType',
            'userLang',
            'user',
            'account',
            'accountToken',
        ]),
        ...mapState('user', {
            globalUserAccountId: 'userAccountId',
            dashboardClient: 'dashboardClient',
        }),
        ...mapState('newDashboard', [
            'widgetError',
            'widgetStatusPanelWidgetID',
            'hubspotBannerHeight',
        ]),
        ...mapGetters('newDashboard', [
            'currentWidgetsById',
            'renderWidgetStatusPanel',
        ]),
        ...mapGetters('view', ['subscriptionWarningVisible']),
        routeMeta: R.pathOr({}, ['$route', 'meta']),
        _accountId() {
            return this.globalUserAccountId || this.cAccountId;
        },
        buildIframeQueryParams() {
            const { queryParams } = this;

            const queries = {
                ...queryParams,
                locale: this.language,
                country: this.user.country,
                token: this.token,
                baseUrl:
                    process.env.VUE_APP_REACT_BASE_URL !==
                    window.location.origin
                        ? window.location.origin
                        : undefined,
            };

            const cleanedQueryParamsEntries = Object.entries(queries).filter(
                ([_, value]) => !R.isNil(value)
            );

            const cleanedQueryParams = Object.fromEntries(
                cleanedQueryParamsEntries
            );
            return new URLSearchParams(cleanedQueryParams).toString();
        },
        iframeUrl() {
            const paramId = this.currentId ?? '';
            const fileExtension =
                process.env.NODE_ENV === `production` ? `.html` : ``;
            const baseUrl = REACT_BASE_URL;

            const queryParams = this.buildIframeQueryParams;

            return `${baseUrl}/automation${fileExtension}?viewId=${paramId}&${queryParams}`;
        },
    },

    render() {
        if (!this.cAccountId) {
            return (
                <PageLoaderWrapper>
                    <RingLoader />
                </PageLoaderWrapper>
            );
        }

        return (
            <div>
                <Iframe
                    src={this.iframeUrl}
                    data-hj-allow-iframe=""
                    ref="iframeReactDashboard_"
                    customWidth="100vw"
                    customHeight={
                        this.isMobileSize
                            ? `100vh`
                            : `calc(100vh - ${this.hubspotBannerHeight ?? 0}px)`
                    }
                    resizing={false}
                    noMinHeight={true}
                    overlayVisible={!this.connectionPanel}
                />
                {this.renderDataSource_()}

                <portal-target name="panel_modal" />
                <portal-target name="panel_modal_second" />
            </div>
        );
    },
};

export default WithAccountModal(ReactAutomation);
